import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import { MapComponent, ReportsSidebar, ReportDetailsPanel } from "./components";
import ReportButton from "./components/ReportButton";
import PhotoSelectionDialog from "./components/PhotoSelectionDialog";
import ReportPage from "./components/report/ReportPage";
import { DataProvider, useDataContext } from "./contexts/DataContext";
import { UserProvider } from "./contexts/UserContext";
import { ChatProvider, useChatContext } from "./contexts/ChatContext";
import ChatModal from "./components/chat/ChatModal";
import { ReportData } from "./types/global.d";
import {
  FilterType,
  filterReportsByStatus,
} from "./components/sidebar/reportsUtils";
import {
  parseReportIdFromPath,
  useReportUrlSync,
} from "./components/hooks/useReportUrlSync";
import { useTelegramChrome } from "./platform/useTelegramChrome";
import { launchReportId } from "./platform/deepLink";
import TelegramAuthBridge from "./components/auth/TelegramAuthBridge";
import { isBridgeMode } from "./api/authBridge";
import VkCallback from "./components/auth/VkCallback";
import AccountMigrationPage from "./components/auth/AccountMigrationPage";

function AppContent() {
  const { t } = useTranslation();
  const { reports, loading, error, loadReportDetails } = useDataContext();
  const { totalUnreadCount } = useChatContext();
  const [selectedReportId, setSelectedReportId] = useState<string | null>(() =>
    parseReportIdFromPath() || launchReportId()
  );
  // When a non-expandable cluster is opened, this holds every report id in it
  // so the details panel can offer a selector to page between them. Empty/one
  // element for a single-pin selection (selector hidden).
  const [previewReportIds, setPreviewReportIds] = useState<string[]>([]);
  const { openReportUrl, closeReportUrl, resetReportUrl } =
    useReportUrlSync(setSelectedReportId);
  const attemptedReportIds = useRef<Set<string>>(new Set());
  const [mapFilter, setMapFilter] = useState<FilterType>("all");
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showReportPage, setShowReportPage] = useState(false);
  const [chatRequest, setChatRequest] = useState<{
    mode: "list" | "thread";
    chatId?: string;
    nickname?: string;
    key: number;
  } | null>(null);

  // Filter reports for the map based on the current filter and user filter
  const filteredReports = useMemo(() => {
    let filtered = filterReportsByStatus(reports, mapFilter);

    // Apply user filter if set
    if (userFilter) {
      filtered = filtered.filter(
        (report) =>
          report.user?.nickname === userFilter ||
          report.cleaned?.user?.nickname === userFilter
      );
    }

    return filtered;
  }, [reports, mapFilter, userFilter]);

  // Find the selected report
  const selectedReport = selectedReportId
    ? reports.find((report) => report._id === selectedReportId) || null
    : null;

  // Resolve the cluster-preview set (in list order) for the details selector.
  const previewReports = useMemo(() => {
    if (previewReportIds.length < 2) return [];
    return previewReportIds
      .map((id) => reports.find((report) => report._id === id))
      .filter((report): report is ReportData => Boolean(report));
  }, [previewReportIds, reports]);

  // Ensure the active report is loaded to its full tier. Covers both deeplink
  // entry (report not in the synced list yet) and selecting a status-only pin.
  useEffect(() => {
    if (!selectedReportId) return;
    if (selectedReport?.dataLevel === "full") return;

    if (!selectedReport) {
      // Deeplink: not in the synced list — fetch it and surface "not found".
      if (attemptedReportIds.current.has(selectedReportId)) return;
      attemptedReportIds.current.add(selectedReportId);
      loadReportDetails(selectedReportId).then((report) => {
        if (!report) {
          alert(t("report.notFound"));
          setSelectedReportId(null);
          resetReportUrl();
        }
      });
      return;
    }

    // In the list but not full yet — upgrade it on demand.
    loadReportDetails(selectedReportId);
  }, [selectedReportId, selectedReport, loadReportDetails, resetReportUrl, t]);

  const handleReportClick = useCallback(
    (report: ReportData) => {
      setPreviewReportIds([]);
      setSelectedReportId(report._id);
      openReportUrl(report._id);
    },
    [openReportUrl]
  );

  // Non-expandable cluster tapped: open a preview over all of its reports.
  const handleClusterReportsSelect = useCallback(
    (reportIds: string[]) => {
      if (!reportIds.length) return;
      setPreviewReportIds(reportIds);
      setSelectedReportId(reportIds[0]);
      openReportUrl(reportIds[0]);
    },
    [openReportUrl]
  );

  // Switch the active report within an open cluster preview.
  const handleSelectPreviewReport = useCallback(
    (reportId: string) => {
      setSelectedReportId(reportId);
      openReportUrl(reportId);
    },
    [openReportUrl]
  );

  const handleClosePanel = useCallback(() => {
    setSelectedReportId(null);
    setPreviewReportIds([]);
    closeReportUrl();
  }, [closeReportUrl]);

  const handleFilterChange = useCallback((filter: FilterType) => {
    setMapFilter(filter);
  }, []);

  const handleUserFilterChange = useCallback((nickname: string | null) => {
    setUserFilter(nickname);
  }, []);

  const handleReportButtonClick = useCallback(() => {
    setShowPhotoDialog(true);
  }, []);

  const handlePhotoDialogClose = useCallback(() => {
    setShowPhotoDialog(false);
  }, []);

  const handlePhotosSelected = useCallback((files: File[]) => {
    setSelectedFiles(files);
    setShowReportPage(true);
  }, []);

  const handleReportPageClose = useCallback(() => {
    setShowReportPage(false);
    setSelectedFiles([]);
  }, []);

  const handleReportSuccess = useCallback(() => {
    setShowReportPage(false);
    setSelectedFiles([]);
    alert(t("report.reportSubmitted"));
  }, [t]);

  const handleOpenChatsList = useCallback(() => {
    setChatRequest({ mode: "list", key: Date.now() });
  }, []);

  const handleOpenChatWithUser = useCallback((nickname: string) => {
    setChatRequest({ mode: "thread", nickname, key: Date.now() });
  }, []);

  const handleCloseChat = useCallback(() => {
    setChatRequest(null);
  }, []);

  const anyPanelOpen =
    showReportPage ||
    showPhotoDialog ||
    !!chatRequest ||
    !!selectedReportId;

  const closeTopmostPanel = useCallback(() => {
    if (showReportPage) {
      handleReportPageClose();
    } else if (showPhotoDialog) {
      handlePhotoDialogClose();
    } else if (chatRequest) {
      handleCloseChat();
    } else if (selectedReportId) {
      handleClosePanel();
    }
  }, [
    showReportPage,
    showPhotoDialog,
    chatRequest,
    selectedReportId,
    handleReportPageClose,
    handlePhotoDialogClose,
    handleCloseChat,
    handleClosePanel,
  ]);

  useTelegramChrome({
    activePanelOpen: anyPanelOpen,
    onCloseActivePanel: closeTopmostPanel,
  });

  return (
    <div className="App">
      <MapComponent
        reports={filteredReports}
        selectedReportId={selectedReportId}
        onReportClick={handleReportClick}
        onClusterReportsSelect={handleClusterReportsSelect}
        onOpenChatsList={handleOpenChatsList}
        chatsListBadgeCount={totalUnreadCount}
      />
      <ReportsSidebar
        reports={reports}
        loading={loading}
        error={error}
        onReportClick={handleReportClick}
        selectedReportId={selectedReportId}
        onFilterChange={handleFilterChange}
        userFilter={userFilter}
        onUserFilterChange={handleUserFilterChange}
      />
      <ReportDetailsPanel
        report={selectedReport}
        previewReports={previewReports}
        onSelectPreviewReport={handleSelectPreviewReport}
        onClose={handleClosePanel}
        onUserFilterChange={handleUserFilterChange}
        onOpenChatWithUser={handleOpenChatWithUser}
      />
      <ReportButton onClick={handleReportButtonClick} />

      {showPhotoDialog && (
        <PhotoSelectionDialog
          onClose={handlePhotoDialogClose}
          onPhotosSelected={handlePhotosSelected}
        />
      )}

      {showReportPage && selectedFiles.length > 0 && (
        <ReportPage
          files={selectedFiles}
          onClose={handleReportPageClose}
          onSuccess={handleReportSuccess}
        />
      )}

      {chatRequest && (
        <ChatModal
          isOpen={true}
          mode={chatRequest.mode}
          chatId={chatRequest.chatId}
          nickname={chatRequest.nickname}
          requestKey={chatRequest.key}
          onClose={handleCloseChat}
        />
      )}
    </div>
  );
}

function App() {
  if (window.location.pathname === "/account-migration") {
    return <AccountMigrationPage />;
  }
  if (window.location.pathname === "/vk/mobile/callback") {
    return <VkCallback />;
  }
  // System-browser social-bridge page (opened from the Mini App). It must not
  // mount the map/data/chat providers — it only completes the auth hand-off.
  if (isBridgeMode()) {
    return <TelegramAuthBridge />;
  }
  return (
    <UserProvider>
      <ChatProvider>
        <DataProvider>
          <AppContent />
        </DataProvider>
      </ChatProvider>
    </UserProvider>
  );
}

export default App;
